import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Tag,
    Edit2,
    Trash2,
    Eye,
    EyeOff,
    Plus,
    Save,
    X,
    AlertTriangle,
    Move,
    CheckCircle,
    Search,
    LayoutGrid,
    Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { ACCESS_LEVELS } from '../../utils/adminUtils';

/**
 * AdminFunctions
 * Permite que admins gerenciem categorias de tickets (título, descrição, visibilidade).
 */
const AdminFunctions = () => {
    const { categories, updateCategory } = useTickets();
    const { rolePermissions, updateRolePermissions } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCategories, setFilteredCategories] = useState([]);

    // Role Permissions State
    const [editingRole, setEditingRole] = useState(null);
    const [selectedRoleCategories, setSelectedRoleCategories] = useState([]);
    const [isSavingPermissions, setIsSavingPermissions] = useState(false);

    // Roles disponíveis para gerenciar (Removidos Admin e Direção pois já têm acesso total via código, e user que é padrão)
    const manageableRoles = Object.keys(ACCESS_LEVELS).filter(role => !['admin', 'direcao', 'user'].includes(role));

    // Edit Modal State
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [editForm, setEditForm] = useState({
        label: '',
        description: '',
        is_active: true
    });
    const [isSaving, setIsSaving] = useState(false);

    // Google Drive Config State
    const [driveConfig, setDriveConfig] = useState({
        root_folder_id: '',
        shared_drive_id: ''
    });
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // Fetch Categories
    useEffect(() => {
        if (categories) {
            const filtered = categories.filter(cat =>
                cat.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                cat.slug.toLowerCase().includes(searchTerm.toLowerCase())
            );
            // Sort by order_index
            filtered.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
            setFilteredCategories(filtered);
        }
    }, [categories, searchTerm]);

    // Fetch Drive Config on Mount
    useEffect(() => {
        const fetchDriveConfig = async () => {
            try {
                const { data, error } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'google_drive_config')
                    .single();

                if (data && data.value) {
                    setDriveConfig({
                        root_folder_id: data.value.root_folder_id || '',
                        shared_drive_id: data.value.shared_drive_id || ''
                    });
                }
            } catch (e) {
                console.error("Error fetching drive config:", e);
            }
        };
        fetchDriveConfig();
    }, []);

    const handleSaveDriveConfig = async () => {
        setIsSavingConfig(true);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'google_drive_config',
                    value: driveConfig,
                    updated_at: new Date()
                });

            if (error) throw error;
            toast.success('Configuração do Google Drive salva!');
        } catch (error) {
            console.error(error);
            toast.error('Erro ao salvar configuração.');
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleEditClick = (category) => {
        setSelectedCategory(category);
        setEditForm({
            label: category.label,
            description: category.description || '',
            is_active: category.is_active
        });
        setIsEditOpen(true);
    };

    const handleSave = async () => {
        if (!selectedCategory) return;
        setIsSaving(true);

        try {
            const result = await updateCategory(selectedCategory.id, {
                label: editForm.label,
                description: editForm.description,
                is_active: editForm.is_active
            });

            if (result.success) {
                toast.success('Categoria atualizada com sucesso!');
                setIsEditOpen(false);
            } else {
                toast.error('Erro ao atualizar categoria.');
            }
        } catch (error) {
            console.error(error);
            toast.error('Erro interno.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleVisibility = async (category, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            const result = await updateCategory(category.id, { is_active: newStatus });

            if (result.success) {
                toast.success(`Categoria ${newStatus ? 'ativada' : 'desativada'} com sucesso.`);
            } else {
                toast.error('Erro ao alterar visibilidade.');
            }
        } catch (error) {
            console.error(error);
            toast.error('Erro interno.');
        }
    };

    // --- Role Permissions Handlers ---
    const handleRoleEdit = (role) => {
        setEditingRole(role);
        // Ensure it's an array
        const currentPerms = rolePermissions[role] || [];
        setSelectedRoleCategories([...currentPerms]);
    };

    const handleRoleCategoryToggle = (categorySlug) => {
        setSelectedRoleCategories(prev => {
            if (prev.includes(categorySlug)) {
                return prev.filter(c => c !== categorySlug);
            } else {
                return [...prev, categorySlug];
            }
        });
    };

    const handleSaveRolePermissions = async () => {
        if (!editingRole) return;
        setIsSavingPermissions(true);

        try {
            // Update the specific role in the entire map
            const newPermissionsMap = {
                ...rolePermissions,
                [editingRole]: selectedRoleCategories
            };

            const result = await updateRolePermissions(newPermissionsMap);

            if (result.success) {
                toast.success(`Permissões para ${ACCESS_LEVELS[editingRole]?.label || editingRole} atualizadas!`);
                setEditingRole(null);
            } else {
                toast.error(result.error || 'Erro ao atualizar permissões');
            }
        } catch (error) {
            console.error(error);
            toast.error('Erro interno ao salvar permissões');
        } finally {
            setIsSavingPermissions(false);
        }
    };

    return (
        <div className="space-y-6 md:p-6 p-4 animate-in fade-in duration-500">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                    <h1 className="text-3xl font-black text-white tracking-tight flex items-center gap-3">
                        <LayoutGrid className="w-8 h-8 text-blue-500" />
                        Gerenciar Funções
                    </h1>
                    <p className="text-gray-400 mt-2 text-lg">
                        Central de configurações e categorias de tickets.
                    </p>
                </div>
            </div>

            {/* Google Drive Configuration Section */}
            <div className="bg-[#0f1115] border border-white/5 rounded-2xl p-6 mb-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[100px] -z-10" />

                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="bg-blue-500/10 p-2 rounded-lg text-blue-400">
                        <Save size={20} />
                    </span>
                    Configurações do Sistema
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-black/20 p-5 rounded-xl border border-white/5 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-white">Google Drive API</h3>
                            <Badge variant="outline" className="text-blue-400 border-blue-500/20 bg-blue-500/10">Upload de Provas</Badge>
                        </div>
                        <p className="text-sm text-gray-400">Defina os IDs para o armazenamento automático de evidências.</p>

                        <div className="space-y-3 pt-2">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Root Folder ID</label>
                                <Input
                                    value={driveConfig.root_folder_id}
                                    onChange={e => setDriveConfig({ ...driveConfig, root_folder_id: e.target.value })}
                                    placeholder="Ex: 1A2B3C..."
                                    className="bg-black/40 border-white/10 text-white font-mono text-sm"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Shared Drive ID (Opcional)</label>
                                <Input
                                    value={driveConfig.shared_drive_id}
                                    onChange={e => setDriveConfig({ ...driveConfig, shared_drive_id: e.target.value })}
                                    placeholder="Ex: 0X9Y8Z..."
                                    className="bg-black/40 border-white/10 text-white font-mono text-sm"
                                />
                            </div>

                            <Button
                                onClick={handleSaveDriveConfig}
                                disabled={isSavingConfig}
                                className="w-full mt-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white border border-blue-500/20"
                            >
                                {isSavingConfig ? (
                                    <>
                                        <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                                        Salvando...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        Salvar Configuração
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Role Permissions Section */}
            <div className="bg-[#0f1115] border border-white/5 rounded-2xl p-6 mb-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[100px] -z-10" />

                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="bg-purple-500/10 p-2 rounded-lg text-purple-400">
                        <Shield size={20} />
                    </span>
                    Gestão de Cargos e Categorias
                </h2>
                <p className="text-sm text-gray-400 mb-6">Defina quais categorias cada cargo da staff tem acesso para visualizar e responder tickets.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {manageableRoles.map(role => {
                        const isEditing = editingRole === role;
                        const roleInfo = ACCESS_LEVELS[role];
                        const perms = isEditing ? selectedRoleCategories : (rolePermissions[role] || []);

                        return (
                            <div key={role} className={`p-5 rounded-xl border transition-all duration-300 ${isEditing ? 'bg-purple-500/5 border-purple-500/50' : 'bg-black/20 border-white/5'}`}>
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${roleInfo.color}-500/20 text-${roleInfo.color}-400`}>
                                            <Shield size={16} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-white">{roleInfo.label}</h3>
                                            <p className="text-xs text-gray-500 font-mono">{role}</p>
                                        </div>
                                    </div>
                                    {!isEditing && (
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white hover:bg-white/5" onClick={() => handleRoleEdit(role)}>
                                            <Edit2 size={14} />
                                        </Button>
                                    )}
                                </div>

                                {isEditing ? (
                                    <div className="space-y-4">
                                        <div className="flex flex-wrap gap-2 mt-4 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                                            {categories?.map(cat => {
                                                const isSelected = selectedRoleCategories.includes(cat.slug);
                                                return (
                                                    <Badge
                                                        key={cat.id}
                                                        variant="outline"
                                                        className={`cursor-pointer transition-colors ${isSelected ? 'bg-purple-500/20 text-purple-300 border-purple-500/50' : 'bg-black/40 text-gray-500 border-white/10 hover:border-gray-500'}`}
                                                        onClick={() => handleRoleCategoryToggle(cat.slug)}
                                                    >
                                                        {cat.label}
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                        <div className="flex gap-2 pt-2 border-t border-white/10">
                                            <Button variant="ghost" size="sm" className="flex-1 text-gray-400 hover:text-white" onClick={() => setEditingRole(null)}>
                                                Cancelar
                                            </Button>
                                            <Button size="sm" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" disabled={isSavingPermissions} onClick={handleSaveRolePermissions}>
                                                {isSavingPermissions ? 'Salvando...' : 'Salvar'}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                        {perms.length > 0 ? (
                                            perms.map(catSlug => {
                                                const catInfo = categories?.find(c => c.slug === catSlug);
                                                return (
                                                    <Badge key={catSlug} variant="outline" className="bg-black/40 text-gray-400 border-white/5 text-[10px]">
                                                        {catInfo ? catInfo.label : catSlug}
                                                    </Badge>
                                                );
                                            })
                                        ) : (
                                            <span className="text-xs text-gray-600 italic">Sem acesso a categorias específicas</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Categories Header & Search */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pt-4 border-t border-white/5">
                <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Tag size={20} className="text-gray-400" />
                        Categorias de Tickets
                    </h2>
                </div>

                <div className="relative w-full md:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
                    <Input
                        type="text"
                        placeholder="Buscar categoria..."
                        className="pl-10 bg-[#0f1115] border-white/5 focus:border-blue-500/50"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Grid of Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                    {filteredCategories.map((category) => (
                        <motion.div
                            key={category.id}
                            layout
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 ${category.is_active
                                ? 'bg-[#0f1115] border-white/5 hover:border-blue-500/30'
                                : 'bg-[#0a0a0c] border-white/5 opacity-70'
                                }`}
                        >
                            <div className={`absolute top-0 left-0 w-1 h-full ${category.is_active ? 'bg-blue-500' : 'bg-gray-700'}`}></div>

                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${category.is_active
                                            ? 'bg-blue-500/10 text-blue-400'
                                            : 'bg-gray-800/50 text-gray-500'
                                            }`}>
                                            <Tag size={20} />
                                        </div>
                                        <div>
                                            <h3 className={`font-bold text-lg ${category.is_active ? 'text-white' : 'text-gray-400 line-through'}`}>
                                                {category.label}
                                            </h3>
                                            <p className="text-xs text-gray-500 font-mono flex items-center gap-1">
                                                Slug: {category.slug}
                                            </p>
                                        </div>
                                    </div>

                                    <Switch
                                        checked={category.is_active}
                                        onCheckedChange={() => handleToggleVisibility(category, category.is_active)}
                                        className="data-[state=checked]:bg-blue-600"
                                    />
                                </div>

                                <p className="text-gray-400 text-sm mb-6 line-clamp-2 h-10">
                                    {category.description || 'Sem descrição definida.'}
                                </p>

                                <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full border-white/10 hover:bg-white/5 hover:text-white"
                                        onClick={() => handleEditClick(category)}
                                    >
                                        <Edit2 className="w-4 h-4 mr-2" />
                                        Editar Detalhes
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {filteredCategories.length === 0 && (
                    <div className="col-span-full py-12 text-center bg-[#0f1115] rounded-2xl border border-dashed border-white/10">
                        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-8 h-8 text-gray-600" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Nenhuma categoria encontrada</h3>
                        <p className="text-gray-400">Tente ajustar seus filtros de busca ou verifique se as categorias foram inicializadas no banco de dados.</p>
                    </div>
                )}
            </div>

            {/* Edit Dialog */}
            <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                <DialogContent className="bg-[#0f1115] border-white/10 text-white sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Editar Categoria</DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Personalize como esta categoria aparece para os usuários.
                            <br />
                            <span className="text-xs text-yellow-500/80 mt-1 block">
                                Nota: O "slug" não pode ser alterado pois é usado pela lógica interna.
                            </span>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Título (Label)</label>
                            <Input
                                value={editForm.label}
                                onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                                className="bg-black/20 border-white/10 focus:border-blue-500/50"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Descrição</label>
                            <Textarea
                                value={editForm.description}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                className="bg-black/20 border-white/10 focus:border-blue-500/50 min-h-[100px]"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5">
                            <div className="flex items-center gap-2">
                                {editForm.is_active ? <Eye className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                                <span className="text-sm font-medium">Visibilidade</span>
                            </div>
                            <Switch
                                checked={editForm.is_active}
                                onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsEditOpen(false)} className="hover:bg-white/5 text-gray-400 hover:text-white">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminFunctions;
